import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import {
  ArrowLeft,
  Plus,
  Search,
  Building2,
  MapPin,
  Globe,
  Phone,
  Mail,
  QrCode,
  Download,
  MoreHorizontal,
  Pencil,
  Trash2,
  Users,
  ChevronDown,
  Filter,
  X,
  Image,
  Tag,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { boothQrCode } from "@mvk/shared";
import type { Company } from "../../types";
import { useAdminAuth } from "../auth/AdminAuthContext";
import { useAdminEvent } from "../hooks/useAdminEvent";
import { CuratorExhibitorsPage } from "./curator/CuratorExhibitorsPage";
import { adminEventsApi, tryLoadExhibitors } from "../../services/adminEventsApi";
import { catalogService } from "../../services/catalogService";
import type { CompanySummary } from "@mvk/shared";
import { ApiError } from "../../api/client";
import { downloadQrPng } from "../../utils/codeImages";
import { printableQr } from "../../utils/publicQr";
import { CompanyAvatar } from "../../components/CompanyAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

interface AddExhibitorModalProps {
  eventId: string;
  onClose: () => void;
  onCreated: () => void;
}

function summaryToCompany(summary: CompanySummary, eventId: string): Company {
  return {
    id: summary.id,
    eventId,
    name: summary.name,
    category: summary.category,
    tags: [],
    hall: summary.hall ?? "",
    booth: summary.booth ?? "",
    shortAbout: summary.shortAbout,
    logoUrl: summary.logoUrl,
    country: summary.country,
    city: summary.city,
    products: [],
    contact: {
      phone: undefined,
      email: undefined,
    },
    qrCodeValue: printableQr(boothQrCode(eventId, summary.id)),
    mapPoint: { x: 0, y: 0, hallId: "" },
  };
}

function AddExhibitorModal({ eventId, onClose, onCreated }: AddExhibitorModalProps) {
  const [step, setStep] = useState<"basic" | "contacts" | "booth">("basic");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [form, setForm] = useState({
    name: "", category: "", country: "", city: "",
    website: "", shortAbout: "",
    phone: "", email: "",
    hall: "", booth: "",
  });

  const steps = [
    { id: "basic", label: "Основное" },
    { id: "contacts", label: "Контакты" },
    { id: "booth", label: "Стенд" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-[15px] text-gray-900">Добавить экспонента</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X size={18} />
          </button>
        </div>

        {/* Steps */}
        <div className="flex border-b border-gray-100">
          {steps.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setStep(s.id as any)}
              className={`flex-1 py-2.5 text-[12px] transition-colors ${
                step === s.id
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] mr-1.5 ${
                step === s.id ? "bg-indigo-100 text-indigo-600" : "bg-gray-100 text-gray-400"
              }`}>{i + 1}</span>
              {s.label}
            </button>
          ))}
        </div>

        <div className="p-5 space-y-3 min-h-[280px]">
          {step === "basic" && (
            <>
              <div className="h-20 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center gap-2 cursor-pointer hover:border-indigo-300 transition-colors">
                <Image size={18} className="text-gray-300" />
                <span className="text-[12px] text-gray-400">Загрузить логотип компании</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-[11px] text-gray-500 mb-1 block">Название компании*</label>
                  <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                    placeholder="ООО «Пример»"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
                </div>
                <div>
                  <label className="text-[11px] text-gray-500 mb-1 block">Категория*</label>
                  <input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
                    placeholder="Мебель и интерьер"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
                </div>
                <div>
                  <label className="text-[11px] text-gray-500 mb-1 block">Страна</label>
                  <input value={form.country} onChange={(e) => setForm({...form, country: e.target.value})}
                    placeholder="Россия"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
                </div>
                <div>
                  <label className="text-[11px] text-gray-500 mb-1 block">Город</label>
                  <input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})}
                    placeholder="Москва"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
                </div>
                <div className="col-span-2">
                  <label className="text-[11px] text-gray-500 mb-1 block">Краткое описание</label>
                  <textarea value={form.shortAbout} onChange={(e) => setForm({...form, shortAbout: e.target.value})}
                    rows={3} placeholder="Чем занимается компания..."
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none" />
                </div>
              </div>
            </>
          )}

          {step === "contacts" && (
            <div className="space-y-3">
              <div>
                <label className="text-[11px] text-gray-500 mb-1 block">Телефон (общий)</label>
                <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                  placeholder="+7 (999) 000-00-00"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 mb-1 block">Email (общий)</label>
                <input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                  placeholder="info@company.ru"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 mb-1 block">Сайт</label>
                <input value={form.website} onChange={(e) => setForm({...form, website: e.target.value})}
                  placeholder="https://company.ru"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
              </div>
            </div>
          )}

          {step === "booth" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-gray-500 mb-1 block">Павильон / зал*</label>
                  <select value={form.hall} onChange={(e) => setForm({...form, hall: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 appearance-none bg-white">
                    <option value="">Выбрать...</option>
                    <option value="П1">Павильон 1</option>
                    <option value="П2">Павильон 2</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-gray-500 mb-1 block">Номер стенда*</label>
                  <input value={form.booth} onChange={(e) => setForm({...form, booth: e.target.value})}
                    placeholder="A-701"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
                </div>
              </div>
              <div className="p-3 rounded-xl bg-indigo-50 text-[12px] text-indigo-700">
                После добавления экспонента вы сможете разместить его стенд на карте павильона.
              </div>
              <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                <div className="text-[12px] text-gray-600 mb-2">QR-коды будут сгенерированы автоматически:</div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-[11px] text-gray-500">
                    <QrCode size={12} className="text-indigo-400" />
                    QR стенда — для сканирования посетителями (общие контакты)
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-gray-500">
                    <QrCode size={12} className="text-emerald-400" />
                    QR бейджей — для представителей (персональные контакты)
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100">
          <button
            onClick={() => {
              if (step === "contacts") setStep("basic");
              if (step === "booth") setStep("contacts");
            }}
            className={`px-4 py-2 rounded-lg text-[13px] text-gray-600 hover:bg-gray-100 transition-colors ${step === "basic" ? "invisible" : ""}`}
          >
            Назад
          </button>
          <button
            onClick={async () => {
              if (step === "basic") setStep("contacts");
              else if (step === "contacts") setStep("booth");
              else {
                if (!form.name.trim() || !form.category.trim()) {
                  setSubmitError("Укажите название и категорию");
                  setStep("basic");
                  return;
                }
                setSubmitting(true);
                setSubmitError("");
                try {
                  await adminEventsApi.createExhibitor(eventId, {
                    name: form.name.trim(),
                    category: form.category.trim(),
                    shortAbout: form.shortAbout.trim() || undefined,
                    booth: form.booth.trim() || undefined,
                    hall: form.hall.trim() || undefined,
                    phone: form.phone.trim() || undefined,
                    email: form.email.trim() || undefined,
                    website: form.website.trim() || undefined,
                    country: form.country.trim() || undefined,
                    city: form.city.trim() || undefined,
                  });
                  onCreated();
                  onClose();
                } catch (err) {
                  setSubmitError(
                    err instanceof ApiError ? "Не удалось создать экспонента" : "Ошибка сети",
                  );
                } finally {
                  setSubmitting(false);
                }
              }
            }}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-[13px] transition-colors"
          >
            {step === "booth" ? (submitting ? "Создание…" : "Создать экспонента") : "Далее →"}
          </button>
        </div>
        {submitError && (
          <p className="px-5 pb-4 text-[12px] text-red-600 text-center">{submitError}</p>
        )}
      </div>
    </div>
  );
}

export function AdminExhibitorsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [hallFilter, setHallFilter] = useState<string | null>(null);
  const [apiCompanies, setApiCompanies] = useState<Company[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [repCounts, setRepCounts] = useState<Record<string, number>>({});
  const { event, loading: eventLoading } = useAdminEvent(id);

  const reloadExhibitors = useCallback(async () => {
    if (!id) return;
    setListLoading(true);
    try {
      const list = await tryLoadExhibitors(id);
      if (list) {
        setApiCompanies(list.map((s) => summaryToCompany(s, id)));
      } else {
        setApiCompanies([]);
      }
    } finally {
      setListLoading(false);
    }
  }, [id]);

  useEffect(() => {
    reloadExhibitors();
  }, [reloadExhibitors]);

  const allCompanies = apiCompanies;

  const halls = useMemo(() => {
    const labels = [...new Set(allCompanies.map((c) => c.hall).filter(Boolean))];
    return labels.map((label) => ({ id: label, eventId: id!, name: label, label, floor: 1 }));
  }, [allCompanies, id]);

  useEffect(() => {
    if (!id || !allCompanies.length) {
      setRepCounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        allCompanies.map(async (c) => {
          try {
            const reps = await catalogService.listRepresentatives(id, c.id, "admin");
            return [c.id, reps.length] as const;
          } catch {
            return [c.id, 0] as const;
          }
        }),
      );
      if (!cancelled) setRepCounts(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [id, allCompanies]);

  const filtered = useMemo(() => {
    return allCompanies.filter((c) => {
      const matchSearch =
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.booth.toLowerCase().includes(search.toLowerCase()) ||
        c.category.toLowerCase().includes(search.toLowerCase());
      const matchHall = !hallFilter || c.hall === hallFilter;
      return matchSearch && matchHall;
    });
  }, [allCompanies, search, hallFilter]);

  const handleDownloadQr = useCallback(async (company: Company) => {
    if (!id) return;
    try {
      const value = company.qrCodeValue || printableQr(boothQrCode(id, company.id));
      await downloadQrPng(value, `${company.name}-stenad`);
      toast.success("QR сохранён");
    } catch {
      toast.error("Не удалось скачать QR");
    }
  }, [id]);

  const handleOpenOnMap = useCallback((companyId: string) => {
    if (!id) return;
    navigate(`/events/${id}/map?company=${encodeURIComponent(companyId)}`);
  }, [id, navigate]);

  const handleDeleteExhibitor = useCallback(async (company: Company) => {
    if (!id) return;
    if (!window.confirm(`Убрать «${company.name}» из этой выставки?`)) return;
    try {
      await adminEventsApi.deleteExhibitor(id, company.id);
      toast.success("Экспонент убран из выставки");
      await reloadExhibitors();
    } catch (err) {
      toast.error(err instanceof ApiError ? "Не удалось удалить" : "Ошибка сети");
    }
  }, [id, reloadExhibitors]);

  // Куратор видит свою версию с чеклистами и статусами
  if (user?.role === "curator") return <CuratorExhibitorsPage />;

  if (eventLoading) {
    return (
      <div className="p-6 flex justify-center text-gray-400">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  if (!event) return <div className="p-6 text-gray-500">Мероприятие не найдено</div>;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-5">
      {showAdd && (
        <AddExhibitorModal
          eventId={id!}
          onClose={() => setShowAdd(false)}
          onCreated={reloadExhibitors}
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(`/events/${id}`)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 flex-shrink-0">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-[18px] sm:text-[19px] text-gray-900">Экспоненты</h1>
          <p className="text-[12px] sm:text-[13px] text-gray-400 mt-0.5 truncate">{event.title} · {allCompanies.length} компаний</p>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          <button className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:border-indigo-200 hover:text-indigo-600 text-[13px] transition-colors">
            <Download size={14} /> Экспорт
          </button>
          <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] transition-colors"
            onClick={() => setShowAdd(true)}>
            <Plus size={14} /> <span className="hidden sm:inline">Добавить</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-2 w-64">
          <Search size={14} className="text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск компании, стенда..."
            className="flex-1 text-[13px] outline-none text-gray-700 placeholder-gray-400" />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[12px] text-gray-400">Павильон:</span>
          {[null, ...halls.map((h) => h.label)].map((l) => (
            <button key={String(l)}
              onClick={() => setHallFilter(l)}
              className={`px-2.5 py-1 rounded-lg text-[12px] transition-colors ${
                hallFilter === l
                  ? "bg-indigo-600 text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-indigo-200"
              }`}>
              {l === null ? "Все" : l}
            </button>
          ))}
        </div>
        <span className="ml-auto text-[12px] text-gray-400 flex items-center gap-2">
          {listLoading && <Loader2 size={12} className="animate-spin" />}
          {filtered.length} из {allCompanies.length}
        </span>
      </div>

      {/* Desktop table / Mobile cards */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {/* Desktop header — hidden on mobile */}
        <div className="hidden md:grid grid-cols-[auto_1fr_140px_160px_100px_80px] border-b border-gray-100 px-4 py-2.5">
          {["", "Компания", "Стенд / Зал", "Контакты", "Представители", ""].map((h, i) => (
            <div key={i} className="text-[11px] text-gray-400 uppercase tracking-wider">{h}</div>
          ))}
        </div>

        <div className="divide-y divide-gray-50">
          {filtered.map((company) => {
            const repCount = repCounts[company.id] ?? 0;
            return (
              <div key={company.id}>
                {/* Desktop row */}
                <div className="hidden md:grid grid-cols-[auto_1fr_140px_160px_100px_80px] px-4 py-3 hover:bg-gray-50 transition-colors items-center">
                  <div className="pr-3">
                    <CompanyAvatar
                      name={company.name}
                      logoUrl={company.logoUrl}
                      size="sm"
                      className="w-9 h-9 rounded-lg"
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] text-gray-900 truncate">{company.name}</div>
                    <div className="text-[11px] text-gray-400 truncate">{company.category}</div>
                    {company.country && <div className="text-[11px] text-gray-300 truncate">{company.country}{company.city ? ` · ${company.city}` : ""}</div>}
                  </div>
                  <div>
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-[12px] text-gray-700">
                      <MapPin size={10} className="text-gray-400" />{company.booth}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5">Пав. {company.hall}</div>
                  </div>
                  <div className="space-y-0.5">
                    {company.contact.phone && <div className="flex items-center gap-1 text-[11px] text-gray-500"><Phone size={10} className="text-gray-300" /><span className="truncate">{company.contact.phone}</span></div>}
                    {company.contact.email && <div className="flex items-center gap-1 text-[11px] text-gray-500"><Mail size={10} className="text-gray-300" /><span className="truncate">{company.contact.email}</span></div>}
                    {!company.contact.phone && !company.contact.email && <span className="text-[11px] text-gray-300">Не указаны</span>}
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-[13px] text-gray-700"><Users size={13} className="text-gray-400" />{repCount}</div>
                    {repCount === 0 && <div className="text-[10px] text-amber-500">Нет представителей</div>}
                  </div>
                  <div className="flex items-center gap-1 justify-end">
                    <button
                      onClick={() => navigate(`/events/${id}/exhibitors/${company.id}`)}
                      className="p-1.5 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-500 transition-colors"
                      aria-label="Редактировать"
                    >
                      <Pencil size={14} />
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors outline-none"
                          aria-label="Действия"
                        >
                          <MoreHorizontal size={14} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44 rounded-xl p-1">
                        <DropdownMenuItem
                          className="gap-2 text-[12px] cursor-pointer"
                          onSelect={() => void handleDownloadQr(company)}
                        >
                          <QrCode size={12} /> Скачать QR
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2 text-[12px] cursor-pointer"
                          onSelect={() => handleOpenOnMap(company.id)}
                        >
                          <MapPin size={12} /> На карте
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          variant="destructive"
                          className="gap-2 text-[12px] cursor-pointer text-red-500 focus:text-red-600"
                          onSelect={() => void handleDeleteExhibitor(company)}
                        >
                          <Trash2 size={12} /> Удалить
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Mobile card */}
                <div className="md:hidden px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-3">
                    <CompanyAvatar
                      name={company.name}
                      logoUrl={company.logoUrl}
                      size="md"
                      className="rounded-xl flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-[14px] text-gray-900">{company.name}</div>
                          <div className="text-[12px] text-gray-400">{company.category}</div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-[11px] text-gray-600">
                            <MapPin size={9} className="text-gray-400" />{company.booth}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-3 text-[11px] text-gray-400">
                          {company.contact.phone && <span className="flex items-center gap-1"><Phone size={10} />{company.contact.phone}</span>}
                          <span className="flex items-center gap-1"><Users size={10} />{repCount} пред.</span>
                        </div>
                        <button
                          onClick={() => navigate(`/events/${id}/exhibitors/${company.id}`)}
                          className="text-[12px] text-indigo-500 hover:text-indigo-700"
                        >
                          Открыть →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
